import { Box, Container, Heading, Input, Text } from "@chakra-ui/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"

import type { ApiError } from "@/client"

interface NewPassword {
  token: string
  new_password: string
}
import { Button } from "@/components/atoms/Button"
import { PublicHeader } from "@/components/layout/PublicHeader"
import { isLoggedIn } from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"
import { confirmPasswordRules, handleError, passwordRules } from "@/utils"

interface NewPasswordForm extends NewPassword {
  confirm_password: string
}

export const Route = createFileRoute("/reset-password")({
  component: ResetPassword,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function ResetPassword() {
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<NewPasswordForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      new_password: "",
    },
  })
  const { showSuccessToast, showErrorToast } = useCustomToast()
  const navigate = useNavigate()

  const resetPassword = async (data: NewPassword) => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")

    if (!token) {
      throw new Error(
        "Missing password reset token. Request a new email and try again.",
      )
    }

    await api.instance.post("/api/v1/auth/password/reset/confirm", {
      token,
      new_password: data.new_password,
    })
  }

  const mutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      showSuccessToast("Password updated successfully.")
      reset()
      navigate({ to: "/login" })
    },
    onError: (err: ApiError) => {
      const errorMessage = handleError(err)
      showErrorToast(errorMessage)
    },
  })

  const onSubmit: SubmitHandler<NewPasswordForm> = async (data) => {
    mutation.mutate(data)
  }

  return (
    <Box minH="100vh">
      <PublicHeader hideAuthButtons />
      <Container
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        h="calc(100vh - 64px)"
        maxW="sm"
        alignItems="stretch"
        justifyContent="center"
        gap={4}
        centerContent
      >
        <Heading size="xl" color="ui.main" textAlign="center" mb={2}>
          Reset Password
        </Heading>
        <Text textAlign="center">
          Please enter your new password and confirm it to reset your password.
        </Text>
        <Input
          type="password"
          {...register("new_password", passwordRules())}
          placeholder="New Password"
        />
        {errors.new_password && (
          <Text color="red.500" fontSize="sm">
            {String(errors.new_password.message)}
          </Text>
        )}

        <Input
          type="password"
          {...register("confirm_password", confirmPasswordRules(getValues))}
          placeholder="Confirm Password"
        />
        {errors.confirm_password && (
          <Text color="red.500" fontSize="sm">
            {errors.confirm_password.message}
          </Text>
        )}
        <Button variant="solid" type="submit">
          Reset Password
        </Button>
      </Container>
    </Box>
  )
}
